import React, {FC} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateAvailabilityStatusesEnum} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownItemBadge, DropdownItemBadges} from './dropdownItemBadges';

/*
 * Props.
 */

interface DropdownItemTeammateBadgesProps {
  availabilityStatus?: TeammateAvailabilityStatusesEnum;
  isNonTeamMember?: boolean;
  isUnverified?: boolean;
  renderAsSecondaryContent?: boolean;
}

/*
 * Component.
 */

export const DropdownItemTeammateBadges: FC<DropdownItemTeammateBadgesProps> = (props) => {
  const {
    availabilityStatus = TeammateAvailabilityStatusesEnum.AVAILABLE,
    isNonTeamMember,
    isUnverified,
    renderAsSecondaryContent,
  } = props;
  const theme = useUserTheme();
  const isTeammateOutOfOffice = availabilityStatus === TeammateAvailabilityStatusesEnum.OUT_OF_OFFICE;
  const isTeammateBusy = availabilityStatus === TeammateAvailabilityStatusesEnum.BUSY;
  const isTeammateAvailable = availabilityStatus === TeammateAvailabilityStatusesEnum.AVAILABLE;

  if (isTeammateAvailable && !isNonTeamMember && !isUnverified) {
    return null;
  }

  // If teammate is out of office and unverified, then only show unverified badge, so we show at most two badges total.
  return (
    <DropdownItemBadges renderAsSecondaryContent={renderAsSecondaryContent}>
      {isUnverified && renderUnverifiedBadge(theme)}
      {!isUnverified && isTeammateOutOfOffice && renderOutOfOfficeBadge(theme)}
      {!isUnverified && isTeammateBusy && renderBusyBadge(theme)}
      {isNonTeamMember && renderNotInTeamBadge(theme)}
    </DropdownItemBadges>
  );
};

/*
 * Helpers.
 */

function renderOutOfOfficeBadge(theme: UserTheme) {
  return (
    <DropdownItemBadge color={theme.palette.red.shade40} backgroundColor={theme.palette.red.shade20}>
      <Trans id="dropdown-item-outofoffice">Out of office</Trans>
    </DropdownItemBadge>
  );
}
function renderBusyBadge(theme: UserTheme) {
  return (
    <DropdownItemBadge color={theme.palette.yellow.shade50} backgroundColor={theme.palette.yellow.shade30}>
      <Trans id="dropdown-item-busy">Busy</Trans>
    </DropdownItemBadge>
  );
}

function renderNotInTeamBadge(theme: UserTheme) {
  return (
    <DropdownItemBadge color={theme.greys.shade80} backgroundColor={theme.alphas.gray20}>
      <Trans id="dropdown-item-notinteam">Not in workspace</Trans>
    </DropdownItemBadge>
  );
}

function renderUnverifiedBadge(theme: UserTheme) {
  return (
    <TooltipCoordinator
      placement="top"
      render={() => (
        <Trans id="dropdown-item-unverified-tooltip">
          This user signed up using domain login and needs to be verified. Any company admin can do so by
          visiting the “Teammates” section of Settings.
        </Trans>
      )}
    >
      <DropdownItemBadge color={theme.greys.shade80} backgroundColor={theme.alphas.gray20}>
        <Trans id="dropdown-item-unverified">Unverified</Trans>
      </DropdownItemBadge>
    </TooltipCoordinator>
  );
}
